import {useSyncExternalStore} from 'react';
import {whenOnline} from '../hooks/useOnline';
import {PreparedPhoto} from './resizeImage';
import {Photo} from '../types';

// "Save first, photos follow": the words are posted, the card appears, and this queue uploads
// the prepared photos one at a time per tell, surviving route changes (not reloads; Phase 2
// moves it to IndexedDB with the offline queue, so nothing here assumes memory beyond the
// interface). Failures retry with backoff, then wait for a tap on Retry.
//
// Wire-up: <UploadQueueBridge /> in App gives it an authenticated fetch and a cache callback.

export type UploadStatus = 'pending' | 'uploading' | 'done' | 'failed';

export interface QueuedPhoto {
    id: string;
    previewUrl: string;
    prepared: Promise<PreparedPhoto>;
    status: UploadStatus;
    attempts: number;
    error: string | null;
}

export interface PostUploadState {
    photos: QueuedPhoto[];
    done: number;
    total: number;
    failed: boolean;
    active: boolean;
}

type Fetcher = (path: string, init?: RequestInit) => Promise<Response>;

interface Wiring {
    fetch: Fetcher;
    onUploaded: (postId: string, photo: Photo) => void;
    onSettled: (postId: string) => void;
}

const BACKOFF_MS = [1000, 3000, 8000];

/**
 * How long to hold off before trying a photo again. A 429 carries Retry-After and we wait
 * exactly that: guessing shorter is what turns a busy minute into a queue hammering the server.
 */
export const waitAfter = (error: unknown, attempt: number): number => {
    const after = (error as {retryAfterSeconds?: number | null} | null)?.retryAfterSeconds;
    if (typeof after === 'number' && after > 0) return Math.min(after, 60) * 1000;
    return BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length) - 1];
};

let wiring: Wiring | null = null;
const queues = new Map<string, QueuedPhoto[]>();
const running = new Set<string>();
const listeners = new Set<() => void>();
let snapshot = new Map<string, PostUploadState>();

const sleep = (ms: number) => new Promise(resolve => window.setTimeout(resolve, ms));

const stateOf = (photos: QueuedPhoto[]): PostUploadState => ({
    photos,
    done: photos.filter(p => p.status === 'done').length,
    total: photos.length,
    failed: photos.some(p => p.status === 'failed'),
    active: photos.some(p => p.status === 'pending' || p.status === 'uploading')
});

const emit = () => {
    snapshot = new Map([...queues].map(([postId, photos]) => [postId, stateOf(photos)]));
    listeners.forEach(l => l());
};

async function uploadOne(postId: string, photo: QueuedPhoto): Promise<Photo> {
    if (!wiring) throw new Error('Upload queue not wired');
    const prepared = await photo.prepared;
    const form = new FormData();
    form.append('file', prepared.main, 'photo.jpg');
    if (prepared.thumb) form.append('thumbnail', prepared.thumb, 'thumb.jpg');
    form.append('width', String(prepared.width));
    form.append('height', String(prepared.height));
    form.append('crc32', String(prepared.crc32));
    const response = await wiring.fetch(`/api/posts/${postId}/photos`, {method: 'POST', body: form});
    if (!response.ok) {
        let message = `Upload failed (${response.status})`;
        try {
            const body = (await response.json()) as {errors?: {message: string}[]};
            message = body.errors?.[0]?.message ?? message;
        } catch {
            // keep the status message
        }
        const error = new Error(message) as Error & {status?: number};
        error.status = response.status;
        throw error;
    }
    return (await response.json()) as Photo;
}

async function drain(postId: string): Promise<void> {
    if (running.has(postId)) return;
    running.add(postId);
    try {
        for (;;) {
            const photos = queues.get(postId) ?? [];
            const next = photos.find(p => p.status === 'pending');
            if (!next) break;
            next.status = 'uploading';
            emit();
            await whenOnline();
            try {
                const uploaded = await uploadOne(postId, next);
                next.status = 'done';
                emit();
                wiring?.onUploaded(postId, uploaded);
            } catch (error) {
                next.attempts += 1;
                const status = (error as {status?: number}).status;
                // 4xx is the server saying no (a bad file, a full tell): no point retrying.
                const permanent = status !== undefined && status >= 400 && status < 500 && status !== 429;
                if (permanent || next.attempts >= BACKOFF_MS.length || (error as Error).name === 'UnreadablePhotoError') {
                    next.status = 'failed';
                    next.error = error instanceof Error ? error.message : 'Upload failed';
                    emit();
                } else {
                    next.status = 'pending';
                    emit();
                    await sleep(waitAfter(error, next.attempts));
                }
            }
        }
    } finally {
        running.delete(postId);
        wiring?.onSettled(postId);
        emit();
    }
}

export const uploadQueue = {
    wire(next: Wiring) {
        wiring = next;
    },
    enqueue(postId: string, photos: {id: string; previewUrl: string; prepared: Promise<PreparedPhoto>}[]) {
        if (photos.length === 0) return;
        const existing = queues.get(postId) ?? [];
        queues.set(postId, [...existing, ...photos.map(p => ({...p, status: 'pending' as UploadStatus, attempts: 0, error: null}))]);
        emit();
        void drain(postId);
    },
    retry(postId: string) {
        const photos = queues.get(postId);
        if (!photos) return;
        for (const p of photos) {
            if (p.status === 'failed') {
                p.status = 'pending';
                p.attempts = 0;
                p.error = null;
            }
        }
        emit();
        void drain(postId);
    },
    /** Drops finished uploads and gives their preview URLs back. */
    forget(postId: string) {
        const photos = queues.get(postId);
        if (!photos) return;
        const left = photos.filter(p => p.status !== 'done');
        photos.filter(p => p.status === 'done').forEach(p => URL.revokeObjectURL(p.previewUrl));
        if (left.length === 0) queues.delete(postId);
        else queues.set(postId, left);
        emit();
    },
    stateFor: (postId: string): PostUploadState | null => snapshot.get(postId) ?? null,
    subscribe(listener: () => void) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    }
};

export const useUploadState = (postId: string | undefined): PostUploadState | null =>
    useSyncExternalStore(
        uploadQueue.subscribe,
        () => (postId ? (snapshot.get(postId) ?? null) : null),
        () => null
    );
